import { logoutClearStore } from '@utils/functions';
import { getToken } from '@utils/tokenStorage';
import axios from 'axios';

import { authLogoutSuccess } from '@/store/slices/auth/authSlice';
import { customTabPreviewBumpRefresh } from '@/store/slices/customTabPreview/customTabPreviewSlice';
import { toastrShowError } from '@/store/toolkit/toastr/toastrActions';

import { store } from '../App';
import { BASE_URL } from './constants';

// const BaseUrlLocal = 'http://localhost:51201';
const BaseUrlProd = 'https://unilink.us';
const BaseUrlDev = 'https://unilink-web-dev.azurewebsites.net';
// const BaseUrlProdTest = 'https://testunilink.azurewebsites.net';
// const BaseUrlProd = 'https://cors-anywhere.herokuapp.com/https://unilink.us';
export const BaseUrlShort = 'unilink.us';

export const BaseUrl = BASE_URL;

const CancelToken = axios.CancelToken;
export const source = CancelToken.source();

const defaultOptions = {
  baseURL: BaseUrl,
  headers: {
    'Content-Type': 'application/json', // 'application/x-www-form-urlencoded'
  },
};

let instance = axios.create(defaultOptions);

instance.interceptors.request.use((config) => {
  const token = getToken();
  config.headers.Authorization = token ? `Bearer ${token}` : '';
  return config;
});

// Any successful mutation of a block endpoint (api/{type}-blocks/…, api/blocks/…,
// api/profiles/{id}/blocks/…) bumps customTabPreview.refreshToken. Custom-tab
// previews self-fetch their items and editors keep items in local state, so this
// is the single signal that tells a mounted preview its data may be stale.
const BLOCK_MUTATION_URL = /(^|\/)(api\/)?([a-z-]+-blocks|blocks)\//i;

instance.interceptors.response.use(
  (response) => {
    const method = response.config?.method?.toLowerCase();
    const url = response.config?.url || '';
    if (method && method !== 'get' && BLOCK_MUTATION_URL.test(url)) {
      // Never let a bookkeeping dispatch turn a successful response into a rejection:
      // `store` is a circular import from App and is undefined until the app has booted.
      try {
        store?.dispatch(customTabPreviewBumpRefresh());
      } catch {
        /* preview refresh is best-effort */
      }
    }
    return response;
  },
  (error) => {
    const statusCode = error.response?.status;
    const requestUrl = error.config?.url || '';

    // 401 on admin endpoints = no admin access, not an invalid token — don't logout
    const isAdminEndpoint = requestUrl.includes('/api/admin/') || requestUrl.includes('api/admin/');

    // 401 on email-auto endpoints is proxied from the email microservice, not a user
    // token failure — must not log the user out.
    const isEmailAutoEndpoint = requestUrl.includes('api/email-auto/');

    if (statusCode === 401 && !isAdminEndpoint && !isEmailAutoEndpoint) {
      const currentToken = store.getState().auth.token;

      if (currentToken) {
        store.dispatch(authLogoutSuccess());
        logoutClearStore();
      }
    }

    // 413 on upload endpoints = storage limit reached → prompt to upgrade
    if (statusCode === 413 && error.response?.data?.error === 'STORAGE_LIMIT_EXCEEDED') {
      store.dispatch(toastrShowError('Storage limit reached. Upgrade your plan to upload more.'));
    }

    return Promise.reject(error);
  },
);

export default instance;
